import React from 'react';
import Tag from '@app/component/tag/Tag';
import MarketRegionStateComparisonOutput from '@app/model/market_area/MarketRegionStateComparisonOutput';
import MarketRegionState from '@app/model/market_area/MarketAreaState';
import MarketRegionStateCluster, { marketRegionStateClusterClasses } from '@app/model/market_area/MarketRegionStateCluster';
import Utilities from '@util/Utilities';
import { Tooltip } from 'react-tooltip';
import { FireIcon } from '@heroicons/react/20/solid';
import SnowFlakeIcon from '@images/icons/snowflake-solid.svg';

export const DisplayMarketAreaStateTag: React.FunctionComponent<{
	output: MarketRegionStateComparisonOutput
}> = ({ output }) => {

	if (!output.current) return <span />;

	const id = `market-area-tooltip-${output.current.id}`;

	const difference = (output.current.demand - (!!output.previous ? output.previous.demand : 0))/output.current.demand;

	return <>
		<span data-tooltip-id={id}>
			<Tag state="none"
					 extraClassName="cursor-pointer">{renderIcon(output.current)}&nbsp;{renderDemand(output.current)}</Tag>
		</span>
		{!!output.previous && <Tooltip
			id={id}
			opacity={100}
			className="z-40 bg-white shadow-2xl rounded text-xs"
			style={{ backgroundColor: 'white' }}>
			<h3 className="py-2 px-3 text-sm font-medium text-gray-900 text-left">Market Demand Trend</h3>
			<dl className="divide-y divide-gray-200 border-t">
				<div className="flex justify-between py-1.5 px-3 text-xs font-medium">
					<dt className="text-gray-500">Today</dt>
					<dd className="text-gray-900">{Utilities.formatDouble(output.current.demand, 0)}%</dd>
				</div>
				<div className="flex justify-between py-1.5 px-3 text-xs font-medium">
					<dt
						className="text-gray-500">{Utilities.dateDifference(output.current.creationDate, output.previous.creationDate)} ago
					</dt>
					<dd className="text-gray-900">{Utilities.formatDouble(output.previous.demand, 0)}%</dd>
				</div>
				<div className="py-1.5 px-3 text-xs font-medium">
					<dt
						className="text-gray-500">{Utilities.formatDouble(Math.abs(difference) * 100, 0)}% {difference > 0 ? 'increase' : 'decrease'} in
						the market demand trend
					</dt>
				</div>
			</dl>
		</Tooltip>}
	</>;

};

const renderDemand = (marketAreaState: MarketRegionState) => {
	return <span className={marketRegionStateClusterClasses[marketAreaState.cluster]}>{Utilities.formatDouble(marketAreaState.demand, 0)}%</span>
};

const renderIcon = (marketAreaState: MarketRegionState) => {
	if (marketAreaState.cluster === MarketRegionStateCluster.VERY_HIGH)
		return <FireIcon className="h-3.5 w-3.5 text-red-500" />;
	else if (marketAreaState.cluster === MarketRegionStateCluster.VERY_LOW)
		return <SnowFlakeIcon className="w-3.5 h-3.5 fill-blue-500" />;
	else return <span className="h-3.5" />;
};

export const renderMarketAreaStateIcon = (marketAreaState: MarketRegionState) => {
	if (marketAreaState.cluster === MarketRegionStateCluster.VERY_HIGH)
		return <a title='Very high market demand'>
			<FireIcon className="h-3.5 w-3.5 text-red-500"/>
		</a>;
	else if (marketAreaState.cluster === MarketRegionStateCluster.VERY_LOW)
		return <a title='Very low market demand'>
			<SnowFlakeIcon className="w-3.5 h-3.5 fill-blue-500"/>
		</a>;
	else return <span className="h-3.5" />;
};